<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed } from "vue";
import { FILLED_ICONS, ICONS, type IconName } from "@/design/icons";

/**
 * Design-system primitive: tokens only, no business knowledge, no network.
 *
 * Um traço do `icons.ts`, desenhado.
 *
 * ── POR QUE ISTO NÃO FERE `design-is-independent` ─────────────────────────
 *
 * Porque ele não sabe o que é "Conversas": sabe que existe um traço chamado
 * `envelope` — o mesmo dado que `icons.ts` já publica, e o mesmo motivo pelo qual
 * aquele arquivo é um mapa e não um componente por tela. Quem decide qual nome
 * desenhar continua sendo a navegação.
 *
 * ── O QUE ELE SUBSTITUI ──────────────────────────────────────────────────
 *
 * Sete atributos repetidos em cada `<svg>` da casca — `viewBox`, `fill`, `stroke`,
 * a espessura, as duas pontas e o `aria-hidden`. Estavam copiados em quatro
 * arquivos e já divergiam: a lateral desenhava a 1.5 e o cabeçalho de um grupo a
 * 2, o que fazia o mesmo conjunto parecer dois. A espessura mora aqui agora, e é
 * a nativa do Lucide: contorno 2 numa grade de 24, que a 16px na tela vira 1,33px
 * — o traço que o conjunto foi desenhado para ter.
 *
 * O tamanho continua sendo do CHAMADOR (`class="size-5"`), porque tamanho é
 * composição: o mesmo sino tem 16px na lateral e 20px numa barra de ferramentas.
 *
 * `aria-hidden` sem exceção: um ícone que precise ser lido está ao lado de um
 * rótulo ou dentro de um controle que tem `aria-label`. Um `<svg>` que fala
 * sozinho duplica o que já foi dito.
 *
 * ── AS DUAS ÚNICAS MANEIRAS DE DESENHAR ──────────────────────────────────
 *
 * Contorno para tudo, e CHEIO para as marcas (`FILLED_ICONS`). Não é estilo: a
 * silhueta do WhatsApp é uma mancha sólida com o fone vazado, e desenhá-la com
 * `fill="none"` mostraria o contorno de uma mancha — outra figura. `evenodd`
 * é o que faz o vazado ser buraco; num traço sem preenchimento ele não custa
 * nada, então vai sempre, e não há um `<path>` por modo.
 *
 * Quem escolhe continua sendo o dado: o chamador pede um NOME, e é `icons.ts`
 * que sabe se aquele nome é marca. Um `prop` "filled" poria essa resposta em
 * cada tela, e uma delas erraria.
 */
const props = defineProps<{ name: IconName; class?: string }>();

const filled = computed(() => FILLED_ICONS.has(props.name));

const classes = computed(() => twMerge("size-4 shrink-0", props.class));
</script>

<template>
	<svg
		viewBox="0 0 24 24"
		:fill="filled ? 'currentColor' : 'none'"
		:stroke="filled ? 'none' : 'currentColor'"
		stroke-width="2"
		stroke-linecap="round"
		stroke-linejoin="round"
		aria-hidden="true"
		:class="classes"
	>
		<path :d="ICONS[name]" fill-rule="evenodd" clip-rule="evenodd" />
	</svg>
</template>
